import type { ReactNode } from 'react';
import React, { useEffect, useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';

import { config } from 'src/config';
import { isBrowser } from 'src/shared/helpers/environment';
import { useNonce } from 'src/shared/hooks';
import type { BranchContext } from 'src/shared/hooks/useBranch';
import { Provider, defaultValue } from 'src/shared/hooks/useBranch';

import { snippet } from './snippet';

interface BranchProps {
  children: ReactNode;
}

const BRANCH_PROP_NAME = 'branch';
const CHECK_INTERVAL_MS = 500; // 500ms
const MAX_LOAD_ATTEMPTS = 5;

const Branch = ({ children }: BranchProps) => {
  const nonce = useNonce();
  const isEnabled = Boolean(config.BRANCH_API_KEY) && isBrowser();

  const [isLoading, setIsLoading] = useState(true);
  const [branchSdk, setBranchSdk] = useState();
  const attempts = useRef(1);

  const waitOnWindowProp = (propName: string) =>
    new Promise(function checkWindowProp(resolve, reject) {
      // @ts-expect-error
      const { [propName]: prop } = window;
      if (prop) {
        resolve(prop);

        return;
      }

      if (attempts.current >= MAX_LOAD_ATTEMPTS) {
        reject(new Error(`Timed out waiting on 'window.${propName}' to load`));

        return;
      }

      setTimeout(
        () => checkWindowProp(resolve, reject),
        attempts.current * CHECK_INTERVAL_MS,
      );

      attempts.current += 1;
    });

  useEffect(() => {
    if (!isEnabled) {
      setIsLoading(false);

      return;
    }

    if (!isLoading || attempts.current > 1) return;

    const main = async () => {
      try {
        const branch = await waitOnWindowProp(BRANCH_PROP_NAME);

        // @ts-expect-error
        setBranchSdk(branch);
      } finally {
        setIsLoading(false);
      }
    };

    void main();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!config.BRANCH_API_KEY || (isBrowser() && window.branch)) {
    // eslint-disable-next-line react/jsx-no-useless-fragment
    return <>{children}</>;
  }

  const contextValue: BranchContext = {
    ...defaultValue,
    Branch: branchSdk,
    isLoading,
  };

  return (
    <Provider value={contextValue}>
      <Helmet>
        <script nonce={nonce}>{snippet(config.BRANCH_API_KEY)}</script>
      </Helmet>
      {children}
    </Provider>
  );
};

export { Branch };
